import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { Trash2, Users } from 'lucide-react';
import EmptyState from '../../components/ui/EmptyState';
import { outreachApi } from '../api/outreachApi';
import ImportWizard from '../components/ImportWizard';
import VerificationPanel from '../components/VerificationPanel';
import '../outreach.css';

const PAGE_SIZE = 50;

export default function ContactsPage() {
  const { t } = useTranslation('common');

  const [lists, setLists] = useState([]);
  // List, query and page in the URL: coming back from a contact should not
  // drop you at the top of an unfiltered list.
  const [params, setParams] = useSearchParams();
  const listFilter = params.get('list') || '';
  const search = params.get('q') || '';
  const page = Math.max(Number(params.get('page') || 0), 0);
  const setParam = (changes) =>
    setParams(
      (current) => {
        const updated = new URLSearchParams(current);
        for (const [key, value] of Object.entries(changes)) {
          if (value) updated.set(key, String(value));
          else updated.delete(key);
        }
        return updated;
      },
      { replace: true }
    );

  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleting, setDeleting] = useState(false);


  const loadLists = useCallback(async () => {
    setLists(await outreachApi.listContactLists());
  }, []);

  const loadContacts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await outreachApi.listContacts({
        listId: listFilter || undefined,
        search: search || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setContacts(data.contacts);
      setTotal(data.total);
      setSelectedIds([]);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [listFilter, search, page]);

  const toggleSelected = (id) =>
    setSelectedIds((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    );

  const deleteSelected = async () => {
    if (!window.confirm(t('outreach.deleteContactsConfirm', { count: selectedIds.length }))) return;
    setDeleting(true);
    setError(null);
    try {
      // One page's worth at most, through the same audited per-contact
      // erasure the GDPR route uses, rather than a second bulk path.
      await Promise.all(selectedIds.map((id) => outreachApi.eraseLead(id)));
      await loadContacts();
      await loadLists();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    loadLists();
  }, [loadLists]);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  const onImported = async () => {
    setShowImport(false);
    await loadLists();
    await loadContacts();
  };

  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

  return (
    <div className="unibox">
      <div className="toolbar">
        <h1 className="toolbar-title">{t('outreach.contacts')}</h1>
        <div className="toolbar-filters">
          <select
            className="unibox-select"
            value={listFilter}
            onChange={(e) => setParam({ list: e.target.value, page: 0 })}
          >
            <option value="">{t('outreach.allLists')}</option>
            {lists.map((list) => (
              <option key={list.id} value={list.id}>
                {list.name} ({list.contact_count})
              </option>
            ))}
          </select>
          <input
            className="unibox-select"
            placeholder={t('outreach.searchEmail')}
            value={search}
            onChange={(e) => setParam({ q: e.target.value, page: 0 })}
          />
        </div>
        <div className="toolbar-actions">
          <Link className="unibox-refresh" to="/outreach">
            {t('nav.outreach', 'Conversations')}
          </Link>
          <button className="unibox-button" onClick={() => setShowImport(true)}>
            {t('outreach.importCsv')}
          </button>
        </div>
      </div>

      {showImport ? (
        <ImportWizard lists={lists} onDone={onImported} onCancel={() => setShowImport(false)} />
      ) : (
        <VerificationPanel listId={listFilter} onVerified={loadContacts} />
      )}

      {error && <div className="unibox-banner">{String(error)}</div>}

      <div className="unibox-detail">
        {loading && <div className="unibox-empty">{t('outreach.loading')}</div>}

        {!loading && contacts.length === 0 && (
          <EmptyState
            icon={<Users size={28} />}
            title={search ? t('outreach.emptyTitle') : t('outreach.noContacts')}
            text={search ? t('outreach.noResults') : t('outreach.noContactsHint')}
          >
            {search ? (
              <button className="unibox-refresh" onClick={() => setParam({ q: '' })}>
                {t('outreach.clearSearch')}
              </button>
            ) : (
              <button className="unibox-button" onClick={() => setShowImport(true)}>
                {t('outreach.importCsv')}
              </button>
            )}
          </EmptyState>
        )}

        {!loading && contacts.length > 0 && (
          <>
            {selectedIds.length > 0 && (
              <div className="bulk-bar">
                <span>{t('outreach.contactsSelected', { count: selectedIds.length })}</span>
                <button className="unibox-page-button" onClick={deleteSelected} disabled={deleting}>
                  <Trash2 size={13} /> {t('outreach.deleteSelected')}
                </button>
              </div>
            )}
            <table className="admin-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th className="bulk-cell">
                    <input
                      type="checkbox"
                      checked={selectedIds.length === contacts.length}
                      onChange={(e) =>
                        setSelectedIds(e.target.checked ? contacts.map((c) => c.id) : [])
                      }
                    />
                  </th>
                  <th>{t('outreach.email')}</th>
                  <th>{t('outreach.name')}</th>
                  <th>{t('outreach.company')}</th>
                  <th>{t('outreach.country')}</th>
                  <th>{t('outreach.verification')}</th>
                  <th>{t('outreach.tags')}</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((contact) => (
                  <tr key={contact.id} className={selectedIds.includes(contact.id) ? 'is-selected' : ''}>
                    <td className="bulk-cell">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(contact.id)}
                        onChange={() => toggleSelected(contact.id)}
                      />
                    </td>
                    <td>{contact.email}</td>
                    <td>{[contact.first_name, contact.last_name].filter(Boolean).join(' ')}</td>
                    <td>{contact.company}</td>
                    <td>{contact.country}</td>
                    <td>
                      <span className={`unibox-badge${contact.verification.status === 'invalid' ? ' is-bounce' : ''}`}>
                        {t(`outreach.verificationStatus.${contact.verification.status}`, contact.verification.status)}
                      </span>
                    </td>
                    <td>{contact.tags.join(', ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="unibox-pagination">
              <button className="unibox-page-button" onClick={() => setParam({ page: page - 1 })} disabled={page === 0}>
                ← {t('outreach.previous')}
              </button>
              <span>
                {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} / {total}
              </span>
              <button className="unibox-page-button" onClick={() => setParam({ page: page + 1 })} disabled={page >= lastPage}>
                {t('outreach.next')} →
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
